import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { adminEspecialidadesQuery } from '../../queries/appQueries';
import type { Especialidad as EspecialidadCompartida } from '../../services/especialidadService';
import {
  Text,
  Group,
  Stack,
  Box,
  Card,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Loader,
  Divider,
  Image,
  Alert,
  Progress,
  List,
  Menu,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
  IconStethoscope,
  IconChevronDown,
  IconChevronUp,
  IconPhoto,
  IconX,
  IconUpload,
  IconFileSpreadsheet,
  IconCheck,
  IconAlertCircle,
  IconSettings,
} from '@tabler/icons-react';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';
import PageSkeleton from '../../components/ui/PageSkeleton';

interface Dificultad { id: number; nombre: string; }
interface Especialidad { id: number; nombre: string; }

interface PreguntaForm {
  id?: number;
  pregunta: string;
  respuestaA: string;
  respuestaB: string;
  respuestaC: string;
  respuestaD: string;
  respuestaCorrecta: string;
  retroalimentacion: string;
  especialidadId: string;
  dificultadId: string;
}

interface CasoDTO {
  id: number;
  caso: string;
  imagen?: string;
  temaId?: number;
  cantidadPreguntas: number;
  fechaHora?: string;
  preguntas?: PreguntaForm[];
}

const BASE = '/api';
const token = () => localStorage.getItem('accessToken') || '';

const preguntaVacia = (): PreguntaForm => ({
  pregunta: '', respuestaA: '', respuestaB: '', respuestaC: '', respuestaD: '',
  respuestaCorrecta: 'a', retroalimentacion: '', especialidadId: '', dificultadId: '',
});

const ClinicalCasesPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const accentBlue = dark ? '#8aa9d6' : '#5c7bb0';
  const accentTeal = dark ? '#86b8a6' : '#4f8a74';
  // Botones negros (filled e outline), igual que en Reactivos.
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  const outlineBtn = { root: { borderColor: textColor, color: textColor, backgroundColor: 'transparent' } };

  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch] = useDebouncedValue(search, 350);

  const [casos, setCasos] = useState<CasoDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 100;
  const especialidadesResult = useQuery(adminEspecialidadesQuery());
  const especialidades = (especialidadesResult.data as EspecialidadCompartida[] | undefined) ?? [];
  const [dificultades] = useState<Dificultad[]>([
    { id: 1, nombre: 'Fácil' }, { id: 2, nombre: 'Intermedio' }, { id: 3, nombre: 'Difícil' },
  ]);

  // Modal crear/editar caso
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCaso, setEditingCaso] = useState<CasoDTO | null>(null);
  const [casoText, setCasoText] = useState('');
  const [casoImagen, setCasoImagen] = useState<string>('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preguntas, setPreguntas] = useState<PreguntaForm[]>([preguntaVacia()]);
  const [expandedCasoId, setExpandedCasoId] = useState<number | null>(null);
  const [casoPreguntasCache, setCasoPreguntasCache] = useState<Record<number, PreguntaForm[]>>({});

  // CSV import
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResultado, setCsvResultado] = useState<{ casosInsertados: number; preguntasInsertadas: number; omitidos: number; errores: string[] } | null>(null);

  // Modal agregar pregunta a caso existente
  const [addPreguntaModal, setAddPreguntaModal] = useState(false);
  const [addPreguntaCasoId, setAddPreguntaCasoId] = useState<number | null>(null);
  // Si está seteado, el modal de pregunta está en modo EDICIÓN (PUT) de esa pregunta.
  const [editPreguntaId, setEditPreguntaId] = useState<number | null>(null);
  const [nuevaPregunta, setNuevaPregunta] = useState<PreguntaForm>(preguntaVacia());
  const [savingPregunta, setSavingPregunta] = useState(false);

  // Recarga desde la página 0 cada vez que cambia la búsqueda (con debounce).
  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const qParam = () => {
    const q = debouncedSearch.trim();
    return q ? `&q=${encodeURIComponent(q)}` : '';
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/casos-estudio?page=0&size=${PAGE_SIZE}${qParam()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setCasos(data.data || []);
      setPage(0);
      setHasMore(!!data.hasMore);
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudieron cargar los casos', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const cargarMasCasos = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await fetch(`${BASE}/casos-estudio?page=${nextPage}&size=${PAGE_SIZE}${qParam()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setCasos(prev => [...prev, ...(data.data || [])]);
      setPage(nextPage);
      setHasMore(!!data.hasMore);
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudieron cargar más casos', color: 'red' });
    } finally {
      setLoadingMore(false);
    }
  };

  const abrirCrear = () => {
    setEditingCaso(null);
    setCasoText('');
    setCasoImagen('');
    setPreguntas([preguntaVacia()]);
    setModalOpen(true);
  };

  const abrirEditar = async (caso: CasoDTO) => {
    setEditingCaso(caso);
    setCasoText(caso.caso);
    setCasoImagen(caso.imagen || '');
    setPreguntas([]);
    setModalOpen(true);
  };

  const handleUploadImagen = async (file: File) => {
    setUploadingImg(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${BASE}/casos-estudio/upload-imagen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await res.json();
      if (data.success) {
        setCasoImagen(data.url);
      } else {
        notifications.show({ title: 'Error', message: data.message, color: 'red' });
      }
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo subir la imagen', color: 'red' });
    } finally {
      setUploadingImg(false);
    }
  };

  const handleGuardarCaso = async () => {
    if (!casoText.trim()) {
      notifications.show({ title: 'Error', message: 'El enunciado del caso es obligatorio', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const preguntasValidas = preguntas.filter(p => p.pregunta.trim());
      const body: any = {
        caso: casoText,
        imagen: casoImagen || null,
        preguntas: preguntasValidas.map(p => ({
          pregunta: p.pregunta,
          respuestaA: p.respuestaA,
          respuestaB: p.respuestaB,
          respuestaC: p.respuestaC,
          respuestaD: p.respuestaD,
          respuestaCorrecta: p.respuestaCorrecta,
          retroalimentacion: p.retroalimentacion,
          especialidadId: p.especialidadId ? Number(p.especialidadId) : null,
          dificultad: p.dificultadId ? { id: Number(p.dificultadId) } : null,
        })),
      };

      const url = editingCaso ? `${BASE}/casos-estudio/${editingCaso.id}` : `${BASE}/casos-estudio`;
      const method = editingCaso ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        notifications.show({ title: 'Éxito', message: editingCaso ? 'Caso actualizado' : 'Caso creado', color: 'green' });
        setModalOpen(false);
        cargarDatos();
      } else {
        notifications.show({ title: 'Error', message: data.message, color: 'red' });
      }
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo guardar el caso', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarCaso = async (id: number) => {
    if (!window.confirm('¿Eliminar este caso y todas sus preguntas?')) return;
    try {
      const res = await fetch(`${BASE}/casos-estudio/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      console.log('DELETE response:', data);
      if (data.success) {
        notifications.show({ title: 'Eliminado', message: 'Caso eliminado', color: 'green' });
        cargarDatos();
      }
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo eliminar', color: 'red' });
    }
  };

  const toggleExpandCaso = async (caso: CasoDTO) => {
    if (expandedCasoId === caso.id) {
      setExpandedCasoId(null);
      return;
    }
    setExpandedCasoId(caso.id);
    if (casoPreguntasCache[caso.id]) return;
    try {
      const res = await fetch(`${BASE}/preguntas-caso/caso/${caso.id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setCasoPreguntasCache(prev => ({ ...prev, [caso.id]: data.data || [] }));
    } catch {}
  };

  const handleEliminarPregunta = async (preguntaId: number, casoId: number) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try {
      const res = await fetch(`${BASE}/preguntas-caso/${preguntaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (data.success) {
        setCasoPreguntasCache(prev => ({
          ...prev,
          [casoId]: prev[casoId].filter(p => p.id !== preguntaId),
        }));
        notifications.show({ title: 'Eliminada', message: 'Pregunta eliminada', color: 'green' });
        cargarDatos();
      }
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo eliminar', color: 'red' });
    }
  };

  const abrirAgregarPregunta = (casoId: number) => {
    setAddPreguntaCasoId(casoId);
    setEditPreguntaId(null);
    setNuevaPregunta(preguntaVacia());
    setAddPreguntaModal(true);
  };

  const abrirEditarPregunta = (p: PreguntaForm, casoId: number) => {
    setAddPreguntaCasoId(casoId);
    setEditPreguntaId(p.id ?? null);
    // Pre-cargar el form con los datos de la pregunta (normalizando la correcta a minúscula).
    setNuevaPregunta({
      id: p.id,
      pregunta: p.pregunta || '',
      respuestaA: p.respuestaA || '',
      respuestaB: p.respuestaB || '',
      respuestaC: p.respuestaC || '',
      respuestaD: p.respuestaD || '',
      respuestaCorrecta: (p.respuestaCorrecta || 'a').toLowerCase(),
      retroalimentacion: p.retroalimentacion || '',
      especialidadId: p.especialidadId ? String(p.especialidadId) : '',
      dificultadId: p.dificultadId ? String(p.dificultadId) : '',
    });
    setAddPreguntaModal(true);
  };

  const handleGuardarNuevaPregunta = async () => {
    if (!nuevaPregunta.pregunta.trim() || !nuevaPregunta.especialidadId) {
      notifications.show({ title: 'Error', message: 'Pregunta y especialidad son obligatorias', color: 'red' });
      return;
    }
    setSavingPregunta(true);
    try {
      const body = {
        pregunta: nuevaPregunta.pregunta,
        respuestaA: nuevaPregunta.respuestaA,
        respuestaB: nuevaPregunta.respuestaB,
        respuestaC: nuevaPregunta.respuestaC,
        respuestaD: nuevaPregunta.respuestaD,
        respuestaCorrecta: nuevaPregunta.respuestaCorrecta,
        retroalimentacion: nuevaPregunta.retroalimentacion,
        especialidadId: Number(nuevaPregunta.especialidadId),
        dificultad: nuevaPregunta.dificultadId ? { id: Number(nuevaPregunta.dificultadId) } : null,
      };
      const esEdicion = editPreguntaId !== null;
      const url = esEdicion
        ? `${BASE}/preguntas-caso/${editPreguntaId}`
        : `${BASE}/casos-estudio/${addPreguntaCasoId}/preguntas`;
      const res = await fetch(url, {
        method: esEdicion ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        notifications.show({
          title: 'Éxito',
          message: esEdicion ? 'Pregunta actualizada' : 'Pregunta agregada',
          color: 'green',
        });
        setAddPreguntaModal(false);
        if (addPreguntaCasoId !== null) {
          setCasoPreguntasCache(prev => {
            const prevList = prev[addPreguntaCasoId] || [];
            const nextList = esEdicion
              ? prevList.map(q => (q.id === editPreguntaId ? data.data : q))
              : [...prevList, data.data];
            return { ...prev, [addPreguntaCasoId]: nextList };
          });
        }
        setEditPreguntaId(null);
        cargarDatos();
      } else {
        notifications.show({ title: 'Error', message: data.message, color: 'red' });
      }
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo guardar la pregunta', color: 'red' });
    } finally {
      setSavingPregunta(false);
    }
  };

  const updatePregunta = (idx: number, field: keyof PreguntaForm, value: string) => {
    setPreguntas(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleImportarCSV = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvResultado(null);
    try {
      const data = new FormData();
      data.append('file', csvFile);
      const res = await fetch('/api/casos-estudio/importar-csv', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: data,
      });
      const result = await res.json();
      console.log('CSV import response:', result);
      if (!res.ok) throw new Error(result.error || 'Error al importar');
      setCsvResultado(result);
      notifications.show({ title: 'Importación exitosa', message: `${result.casosInsertados} casos y ${result.preguntasInsertadas} preguntas importadas`, color: 'green' });
      cargarDatos();
    } catch (e: any) {
      notifications.show({ title: 'Error', message: e.message, color: 'red' });
    } finally {
      setCsvUploading(false);
    }
  };

  return (
    <PageTransition type="medical" duration={800}>
    <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <AdminPageHeader
            title="Casos Clínicos"
            subtitle="Gestiona casos clínicos y sus preguntas"
          />
          <Group>
            <Button leftSection={<IconUpload size={16} />} variant="default" styles={outlineBtn}
              onClick={() => { setCsvFile(null); setCsvResultado(null); setCsvModalOpen(true); }}>
              Importar CSV
            </Button>
            <Button leftSection={<IconPlus size={16} />} styles={primaryBtn} onClick={abrirCrear}>
              Nuevo Caso
            </Button>
          </Group>
        </Group>

        <TextInput
          placeholder="Buscar por texto del caso o de la pregunta…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          rightSection={
            search ? (
              <IconX
                size={16}
                style={{ cursor: 'pointer' }}
                onClick={() => setSearch('')}
              />
            ) : null
          }
        />

        {loading ? (
          <PageSkeleton variant="list" count={5} />
        ) : casos.length === 0 ? (
          <Card withBorder radius="lg" p="xl" style={cardStyle}>
            <Stack align="center" gap="md">
              <IconStethoscope size={48} style={{ opacity: 0.3, color: mutedColor }} />
              <Text style={{ color: mutedColor }}>No hay casos clínicos. Crea el primero.</Text>
            </Stack>
          </Card>
        ) : (
          <Stack gap="md">
            {casos.map(caso => (
              <Card key={caso.id} radius="lg" withBorder p="lg" style={cardStyle}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="md">
                      <Text size="xs" fw={600} style={{ color: accentBlue }}>
                        {caso.cantidadPreguntas} preguntas
                      </Text>
                      {caso.imagen && (
                        <Group gap={4}>
                          <IconPhoto size={12} color={accentTeal} />
                          <Text size="xs" fw={600} style={{ color: accentTeal }}>Imagen</Text>
                        </Group>
                      )}
                      {caso.fechaHora && (
                        <Text size="xs" style={{ color: mutedColor }}>
                          {new Date(caso.fechaHora).toLocaleDateString('es-MX')}
                        </Text>
                      )}
                    </Group>
                    <Text size="sm" ta="left" lineClamp={3} style={{ whiteSpace: 'pre-wrap', color: textColor }}>
                      {caso.caso}
                    </Text>
                  </Stack>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    <ActionIcon variant="subtle" color="gray" onClick={() => toggleExpandCaso(caso)} aria-label="Expandir">
                      {expandedCasoId === caso.id ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </ActionIcon>
                    <Menu shadow="md" width={160} position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                          <IconSettings size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => abrirEditar(caso)}>
                          Editar
                        </Menu.Item>
                        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => handleEliminarCaso(caso.id)}>
                          Eliminar
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Group>

                {expandedCasoId === caso.id && (
                  <Box mt="md">
                    <Divider mb="md" />
                    <Group align="flex-start" wrap="nowrap" gap="lg">
                    {caso.imagen && (
                      <Image
                        src={caso.imagen}
                        alt="Imagen del caso"
                        w={300}
                        maw="38%"
                        radius="md"
                        style={{ flexShrink: 0, position: 'sticky', top: 0 }}
                      />
                    )}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                    {!casoPreguntasCache[caso.id] ? (
                      <Group justify="center"><Loader size="sm" color="gray" /></Group>
                    ) : casoPreguntasCache[caso.id].length === 0 ? (
                      <Text size="sm" style={{ color: mutedColor }}>Sin preguntas aún.</Text>
                    ) : (
                      <Stack gap="sm">
                        {casoPreguntasCache[caso.id].map((p, idx) => (
                          <Card key={p.id ?? idx} withBorder radius="md" p="sm" style={cardStyle}>
                            <Group justify="space-between" align="flex-start">
                              <Stack gap={4} align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={600} ta="left" style={{ color: textColor }}>{idx + 1}. {p.pregunta}</Text>
                                <Text size="xs" ta="left" style={{ color: textColor }}>A) {p.respuestaA}</Text>
                                <Text size="xs" ta="left" style={{ color: textColor }}>B) {p.respuestaB}</Text>
                                <Text size="xs" ta="left" style={{ color: textColor }}>C) {p.respuestaC}</Text>
                                <Text size="xs" ta="left" style={{ color: textColor }}>D) {p.respuestaD}</Text>
                                <Text size="xs" fw={600} ta="left" style={{ color: accentTeal }}>
                                  Correcta: {(p.respuestaCorrecta || '').toUpperCase()}
                                </Text>
                                {p.retroalimentacion && (
                                  <Text size="xs" ta="left" style={{ color: mutedColor }}>Retroalimentación: {p.retroalimentacion}</Text>
                                )}
                              </Stack>
                              <Group gap={6} wrap="nowrap">
                                <ActionIcon
                                  variant="light"
                                  color="gray"
                                  size="sm"
                                  onClick={() => abrirEditarPregunta(p, caso.id)}
                                  aria-label="Editar pregunta"
                                >
                                  <IconEdit size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="light"
                                  color="red"
                                  size="sm"
                                  onClick={() => p.id !== undefined && handleEliminarPregunta(p.id, caso.id)}
                                  aria-label="Eliminar pregunta"
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Group>
                            </Group>
                          </Card>
                        ))}
                      </Stack>
                    )}
                    <Button
                      size="xs"
                      variant="default"
                      styles={outlineBtn}
                      mt="sm"
                      leftSection={<IconPlus size={12} />}
                      onClick={() => abrirAgregarPregunta(caso.id)}
                    >
                      Agregar pregunta
                    </Button>
                    </Box>
                    </Group>
                  </Box>
                )}
              </Card>
            ))}
          </Stack>
        )}

        {!loading && hasMore && (
          <Group justify="center">
            <Button variant="default" styles={outlineBtn} loading={loadingMore} onClick={cargarMasCasos}>
              Cargar más casos
            </Button>
          </Group>
        )}
      </Stack>

      {/* Modal crear/editar caso */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCaso ? 'Editar Caso Clínico' : 'Nuevo Caso Clínico'}
        size="xl"
      >
        <Stack gap="md">
          <Textarea
            label="Enunciado del caso"
            placeholder="Describe el caso clínico..."
            minRows={4}
            autosize
            required
            value={casoText}
            onChange={e => setCasoText(e.currentTarget.value)}
          />

          {/* Upload imagen */}
          <Box>
            <Text size="sm" fw={500} mb={4}>Imagen del caso (opcional)</Text>
            {casoImagen ? (
              <Group>
                <Image src={casoImagen} maw={200} radius="md" />
                <ActionIcon color="red" variant="light" onClick={() => setCasoImagen('')}>
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            ) : (
              <Group>
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Subir imagen del caso"
                  onChange={e => e.target.files?.[0] && handleUploadImagen(e.target.files[0])}
                  id="img-upload"
                  hidden
                />
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconPhoto size={14} />}
                  loading={uploadingImg}
                  onClick={() => document.getElementById('img-upload')?.click()}
                >
                  Subir imagen
                </Button>
              </Group>
            )}
          </Box>

          {!editingCaso && (
            <>
              <Divider label="Preguntas del caso" labelPosition="center" />
              {preguntas.map((p, idx) => (
                <Card key={idx} withBorder radius="sm" p="md">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>Pregunta {idx + 1}</Text>
                    {preguntas.length > 1 && (
                      <ActionIcon color="red" variant="light" size="sm"
                        onClick={() => setPreguntas(prev => prev.filter((_, i) => i !== idx))}>
                        <IconX size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                  <Stack gap="xs">
                    <Textarea label="Pregunta" minRows={2} autosize value={p.pregunta}
                      onChange={e => updatePregunta(idx, 'pregunta', e.currentTarget.value)} />
                    <TextInput label="Opción A" value={p.respuestaA}
                      onChange={e => updatePregunta(idx, 'respuestaA', e.currentTarget.value)} />
                    <TextInput label="Opción B" value={p.respuestaB}
                      onChange={e => updatePregunta(idx, 'respuestaB', e.currentTarget.value)} />
                    <TextInput label="Opción C" value={p.respuestaC}
                      onChange={e => updatePregunta(idx, 'respuestaC', e.currentTarget.value)} />
                    <TextInput label="Opción D" value={p.respuestaD}
                      onChange={e => updatePregunta(idx, 'respuestaD', e.currentTarget.value)} />
                    <Select label="Respuesta correcta" value={p.respuestaCorrecta}
                      onChange={v => updatePregunta(idx, 'respuestaCorrecta', v || 'a')}
                      data={[
                        { value: 'a', label: 'A' }, { value: 'b', label: 'B' },
                        { value: 'c', label: 'C' }, { value: 'd', label: 'D' },
                      ]} />
                    <Textarea label="Retroalimentación" minRows={2} autosize value={p.retroalimentacion}
                      onChange={e => updatePregunta(idx, 'retroalimentacion', e.currentTarget.value)} />
                    <Select label="Especialidad" required value={p.especialidadId}
                      onChange={v => updatePregunta(idx, 'especialidadId', v || '')}
                      data={especialidades.map(e => ({ value: String(e.id), label: e.nombre }))} />
                    <Select label="Dificultad" value={p.dificultadId}
                      onChange={v => updatePregunta(idx, 'dificultadId', v || '')}
                      data={dificultades.map(d => ({ value: String(d.id), label: d.nombre }))} />
                  </Stack>
                </Card>
              ))}
              <Button variant="light" leftSection={<IconPlus size={14} />}
                onClick={() => setPreguntas(prev => [...prev, preguntaVacia()])}>
                Agregar otra pregunta
              </Button>
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button loading={saving} onClick={handleGuardarCaso}>
              {editingCaso ? 'Guardar cambios' : 'Crear caso'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal agregar pregunta a caso existente */}
      <Modal
        opened={addPreguntaModal}
        onClose={() => { setAddPreguntaModal(false); setEditPreguntaId(null); }}
        title={editPreguntaId !== null ? 'Editar pregunta' : 'Agregar pregunta al caso'}
        size="lg"
      >
        <Stack gap="xs">
          <Textarea label="Pregunta" minRows={2} autosize required value={nuevaPregunta.pregunta}
            onChange={e => setNuevaPregunta(p => ({ ...p, pregunta: e.currentTarget.value }))} />
          <TextInput label="Opción A" value={nuevaPregunta.respuestaA}
            onChange={e => setNuevaPregunta(p => ({ ...p, respuestaA: e.currentTarget.value }))} />
          <TextInput label="Opción B" value={nuevaPregunta.respuestaB}
            onChange={e => setNuevaPregunta(p => ({ ...p, respuestaB: e.currentTarget.value }))} />
          <TextInput label="Opción C" value={nuevaPregunta.respuestaC}
            onChange={e => setNuevaPregunta(p => ({ ...p, respuestaC: e.currentTarget.value }))} />
          <TextInput label="Opción D" value={nuevaPregunta.respuestaD}
            onChange={e => setNuevaPregunta(p => ({ ...p, respuestaD: e.currentTarget.value }))} />
          <Select label="Respuesta correcta" value={nuevaPregunta.respuestaCorrecta}
            onChange={v => setNuevaPregunta(p => ({ ...p, respuestaCorrecta: v || 'a' }))}
            data={[
              { value: 'a', label: 'A' }, { value: 'b', label: 'B' },
              { value: 'c', label: 'C' }, { value: 'd', label: 'D' },
            ]} />
          <Textarea label="Retroalimentación" minRows={2} autosize value={nuevaPregunta.retroalimentacion}
            onChange={e => setNuevaPregunta(p => ({ ...p, retroalimentacion: e.currentTarget.value }))} />
          <Select label="Especialidad" required value={nuevaPregunta.especialidadId}
            onChange={v => setNuevaPregunta(p => ({ ...p, especialidadId: v || '' }))}
            data={especialidades.map(e => ({ value: String(e.id), label: e.nombre }))} />
          <Select label="Dificultad" value={nuevaPregunta.dificultadId}
            onChange={v => setNuevaPregunta(p => ({ ...p, dificultadId: v || '' }))}
            data={dificultades.map(d => ({ value: String(d.id), label: d.nombre }))} />
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => { setAddPreguntaModal(false); setEditPreguntaId(null); }}>Cancelar</Button>
            <Button loading={savingPregunta} onClick={handleGuardarNuevaPregunta}>
              {editPreguntaId !== null ? 'Guardar cambios' : 'Guardar pregunta'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Importar CSV */}
      <Modal
        opened={csvModalOpen}
        onClose={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}
        title="Importar Casos Clínicos desde CSV"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert color="blue" icon={<IconFileSpreadsheet size={16} />}>
            <Text size="sm" fw={600} mb={4}>Formato esperado del CSV:</Text>
            <Text size="xs" style={{ fontFamily: 'monospace' }}>
              CASO_NUM, CASO, ESPECIALIDAD_ID, TEMA_ID, PREGUNTA, RESPUESTA_A, RESPUESTA_B, RESPUESTA_C, RESPUESTA_D, RESPUESTA_CORRECTA, RETROALIMENTACION, DIFICULTAD_ID
            </Text>
            <Text size="xs" mt={4} c="dimmed">
              Las preguntas del mismo caso comparten el mismo CASO_NUM.<br />
              RESPUESTA_CORRECTA: a, b, c o d (minúscula). DIFICULTAD_ID: 1=Fácil, 2=Intermedio, 3=Difícil.
            </Text>
          </Alert>

          {!csvResultado ? (
            <>
              <Box
                component="label"
                htmlFor="csv-upload-casos"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', minHeight: 100,
                  border: '2px dashed #cbd5e1', borderRadius: 8,
                  cursor: 'pointer', gap: 8,
                }}
              >
                <IconUpload size={32} color="gray" />
                <Text size="sm" fw={500}>Haz clic para seleccionar un archivo CSV</Text>
                <Text size="xs" c="dimmed">Solo archivos .csv</Text>
                <input
                  id="csv-upload-casos"
                  type="file"
                  accept=".csv"
                  title="Seleccionar archivo CSV de casos clínicos"
                  aria-label="Seleccionar archivo CSV de casos clínicos"
                  className="csv-hidden-input"
                  onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                  disabled={csvUploading}
                />
              </Box>

              {csvFile && (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  Archivo seleccionado: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
                </Alert>
              )}

              {csvUploading && <Progress value={100} animated />}

              <Group justify="flex-end">
                <Button variant="light" onClick={() => setCsvModalOpen(false)} disabled={csvUploading}>Cancelar</Button>
                <Button leftSection={<IconUpload size={16} />} onClick={handleImportarCSV} disabled={!csvFile} loading={csvUploading} color="green">
                  Importar
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Alert color={csvResultado.errores.length > 0 ? 'yellow' : 'green'} icon={<IconCheck size={16} />}>
                <Text fw={600}>Importación completada</Text>
                <Text size="sm">{csvResultado.casosInsertados} casos importados</Text>
                <Text size="sm">{csvResultado.preguntasInsertadas} preguntas importadas</Text>
                <Text size="sm">{csvResultado.omitidos} filas omitidas</Text>
              </Alert>
              {csvResultado.errores.length > 0 && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Errores:">
                  <List size="xs">
                    {csvResultado.errores.map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                  </List>
                </Alert>
              )}
              <Group justify="flex-end">
                <Button onClick={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}>Cerrar</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Box>
    </PageTransition>
  );
};

export default ClinicalCasesPage;
